import { DateTime } from "luxon";
import { SALON_TZ } from "@/api/config";
import type { WeeklyHours, Weekday } from "@/api/contract";

export function money(cents: number, from = false): string {
  const dollars = cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;
  return from ? `${dollars}+` : dollars;
}

export function duration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h} hr ${m} min` : `${h} hr`;
}

export function inSalon(iso: string): DateTime {
  return DateTime.fromISO(iso, { zone: "utc" }).setZone(SALON_TZ);
}

export function salonToday(): string {
  return DateTime.now().setZone(SALON_TZ).toISODate()!;
}

export function addDays(date: string, days: number): string {
  return DateTime.fromISO(date).plus({ days }).toISODate()!;
}

export function dayLabel(date: string): { weekday: string; day: string; month: string } {
  const d = DateTime.fromISO(date);
  return { weekday: d.toFormat("ccc"), day: d.toFormat("d"), month: d.toFormat("LLL") };
}

export function longDate(date: string): string {
  return DateTime.fromISO(date).toFormat("cccc, LLLL d");
}

export function timeLabel(iso: string): string {
  return inSalon(iso).toFormat("h:mm a");
}

export function whenLabel(iso: string): string {
  return inSalon(iso).toFormat("cccc, LLLL d 'at' h:mm a");
}

export function hhmmLabel(hhmm: string): string {
  return DateTime.fromFormat(hhmm, "HH:mm").toFormat("h:mm a").replace(":00", "");
}

export function phoneLabel(e164: string): string {
  const d = e164.replace(/^\+1/, "").replace(/\D/g, "");
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : e164;
}

/** Formats as the user types: (972) 924-3242. */
export function formatPhoneInput(value: string): string {
  const d = value.replace(/\D/g, "").replace(/^1/, "").slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

export function isValidUsPhone(value: string): boolean {
  return /^[2-9]\d{2}[2-9]\d{6}$/.test(value.replace(/\D/g, "").replace(/^1(?=\d{10}$)/, ""));
}

export const WEEK: { key: Weekday; label: string; short: string }[] = [
  { key: "mon", label: "Monday", short: "Mon" },
  { key: "tue", label: "Tuesday", short: "Tue" },
  { key: "wed", label: "Wednesday", short: "Wed" },
  { key: "thu", label: "Thursday", short: "Thu" },
  { key: "fri", label: "Friday", short: "Fri" },
  { key: "sat", label: "Saturday", short: "Sat" },
  { key: "sun", label: "Sunday", short: "Sun" },
];

export function hoursLabel(hours: WeeklyHours, day: Weekday): string {
  const ranges = hours[day];
  if (!ranges?.length) return "Closed";
  return ranges.map((r) => `${hhmmLabel(r.start)} – ${hhmmLabel(r.end)}`).join(", ");
}

export function isOpenNow(hours: WeeklyHours): boolean {
  const now = DateTime.now().setZone(SALON_TZ);
  const key = WEEK[now.weekday - 1]!.key;
  const hhmm = now.toFormat("HH:mm");
  return (hours[key] ?? []).some((r) => r.start <= hhmm && hhmm < r.end);
}
